import React, { ChangeEventHandler, useRef } from "react";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrIcon } from "./CrIcon";
import { HbParagraph } from "./HbParagraph";
import { throttle } from "lodash";

interface HbHorizontalSelectProps {
  optionsItems: Array<{
    value: string | number | null;
    label: string | null;
    calcValue?: string | number | null;
  }>;
  currentValue?: string | number | null;
  disabled?: boolean;
  descriptions?: {
    start?: string;
    middle?: string;
    end?: string;
  };
  itemId: string | number;
  fontSize: "SMALL" | "MEDIUM" | "LARGE";
  onChangeValue: (value: string | number | null, calcValue?: string | number) => void;
}

export function HbHorizontalSelect({
  itemId,
  optionsItems,
  currentValue,
  disabled,
  descriptions,
  fontSize,
  onChangeValue,
}: HbHorizontalSelectProps) {
  const ulRef = useRef<HTMLUListElement>(null);
  const descriptionLength = Object.keys(descriptions || []).length;

  const setValue = (value: string | number, calcValue?: string | number): void => {
    if (disabled) return;

    onChangeValue(value, calcValue);
  };

  const handleChangeRadio: ChangeEventHandler = (e) => {
    const optionItem = optionsItems.find((item) => `${itemId}-${item.value}` === e.currentTarget.id);
    const value = optionItem?.value;
    const calcValue = optionItem?.calcValue;

    setValue(value, calcValue);
  };

  const handleTouchMove = throttle((e) => {
    if (optionsItems.length < 5) return;

    const listLeftEnd = ulRef.current?.getBoundingClientRect().left || 0;
    const listRightEnd = ulRef.current?.getBoundingClientRect().right || 0;
    const touchX = e.targetTouches[0].clientX;
    const optionWidth = (ulRef.current?.getBoundingClientRect()?.width || 0) / Math.max(optionsItems.length, 5);

    if (touchX < listLeftEnd) {
      setValue(optionsItems[0].value, optionsItems[0].calcValue);
    } else if (touchX > listRightEnd) {
      setValue(optionsItems[optionsItems.length - 1].value, optionsItems[optionsItems.length - 1].calcValue);
    } else {
      setValue(
        optionsItems[Math.floor((touchX - listLeftEnd) / optionWidth)].value,
        optionsItems[Math.floor((touchX - listLeftEnd) / optionWidth)].calcValue
      );
    }
  }, 50);

  const descriptionFontSize = fontSize === "LARGE" ? "4X-LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : undefined;

  return (
    <>
      <ul className="hb-horizontal-select-list" ref={ulRef} onTouchMove={handleTouchMove}>
        {optionsItems.map((item, itemIdx) => (
          <li
            key={itemIdx}
            className={classNames({
              "hb-horizontal-select-item": true,
              "--selected": currentValue === item.value,
              "font--small": fontSize === "SMALL",
              "font--medium": fontSize === "MEDIUM",
              "font--large": fontSize === "LARGE",
            })}
            style={{ width: `calc(100% / ${Math.max(optionsItems.length, 5)})` }}
          >
            <input
              type="radio"
              id={`${itemId}-${item.value}`}
              value={item.value}
              name="horizontal-select"
              onChange={handleChangeRadio}
              checked={currentValue === item.value}
            />
            <label htmlFor={`${itemId}-${item.value}`}>
              <span>{item.label}</span>
            </label>
          </li>
        ))}
      </ul>
      {descriptions && (
        <div
          className={classNames([
            "hb-horizontal-select-description",
            descriptionLength === 3 ? "---thirds" : descriptionLength === 2 ? "--half" : undefined,
          ])}
        >
          {descriptions.start && (
            <div className="--start">
              <CrIcon icon="Back" />
              <HbParagraph text={descriptions.start} size={descriptionFontSize} />
            </div>
          )}
          {descriptions.middle && (
            <div className="--middle">
              <HbParagraph text={descriptions.middle} size={descriptionFontSize} />
            </div>
          )}
          {descriptions.end && (
            <div className="--end">
              <CrIcon icon="Forward" />
              <HbParagraph text={descriptions.end} size={descriptionFontSize} />
            </div>
          )}
        </div>
      )}
    </>
  );
}
